import { modApi } from "./api";
import { opportunityAPI } from "./opportunityService";

export interface DashboardMetrics {
  totalContacts: number;
  totalCompanies: number;
  totalOpportunities: number;
  totalQuotations: number;
  opportunitiesByStage: Array<{
    stage_id: string;
    count: number;
    total_value: string;
    stage: {
      id: string;
      name: string;
      color: string;
      stage_type: string;
    };
  }>;
  recentOpportunities: Array<{
    id: string;
    slug: string;
    name: string;
    estimated_value: string;
    stage: {
      id: string;
      name: string;
      color: string;
    };
    client_company: {
      id: string;
      name: string;
    };
    estimated_close_date: string;
    success_probability: number;
  }>;
}

export interface SalesFunnelStage {
  stage_id: string;
  stage_name: string;
  stage_color: string;
  stage_type: string;
  opportunities_count: number;
  total_value: number;
  conversion_rate: number | string;
}

export interface SalesFunnelData {
  stages: SalesFunnelStage[];
  dateFrom: string;
  dateTo: string;
}

export interface ContactStatusReport {
  contact_status_id: string;
  count: number;
  contact_status: {
    id: string;
    name: string;
  };
}

export interface ContactsReportData {
  report: ContactStatusReport[];
  dateFrom: string;
  dateTo: string;
}

export const dashboardService = {
  getMetrics: async (companySlug: string): Promise<DashboardMetrics> => {
    try {
      const response = await modApi.get(
        `/dashboard/metrics?slug=${companySlug}`,
      );
      const responseData =
        response.data?.body?.data || response.data?.data || response.data;

      if (!responseData) {
        throw new Error("Invalid response structure from API");
      }

      return {
        totalContacts: responseData.counts?.contacts || 0,
        totalCompanies: responseData.counts?.companies || 0,
        totalOpportunities: responseData.counts?.opportunities || 0,
        totalQuotations: responseData.counts?.quotations || 0,
        opportunitiesByStage: responseData.opportunities_by_stage || [],
        recentOpportunities: responseData.recent_opportunities || [],
      };
    } catch (error) {
      console.error("Error fetching dashboard metrics:", error);
      throw error;
    }
  },
  getSalesFunnel: async (
    companySlug: string,
    dateFrom: string,
    dateTo: string,
  ): Promise<SalesFunnelData> => {
    try {
      const response = await modApi.get(
        `/dashboard/funnel?slug=${companySlug}&date_from=${dateFrom}&date_to=${dateTo}`,
      );

      const responseData =
        response.data?.body?.data || response.data?.data || response.data;

      if (!responseData) {
        throw new Error("Invalid response structure from API");
      }

      return {
        stages: responseData,
        dateFrom,
        dateTo,
      };
    } catch (error) {
      console.error("Error fetching sales funnel:", error);
      throw error;
    }
  },
  getOpportunities: async (slug: string): Promise<any[]> => {
    try {
      const response = await opportunityAPI.getOpportunities({
        slug,
        limit: 1000,
      });
      return response.data || [];
    } catch (error) {
      console.error("Error fetching opportunities:", error);
      return [];
    }
  },

  getContactsReport: async (
    companySlug: string,
    dateFrom: string,
    dateTo: string,
  ): Promise<ContactsReportData> => {
    try {
      const response = await modApi.get(
        `/dashboard/reports/contacts?slug=${companySlug}&group_by=status&date_from=${dateFrom}&date_to=${dateTo}`,
      );

      const responseData =
        response.data?.body?.data || response.data?.data || response.data;

      // Manejo flexible si la respuesta es directamente el array o un objeto que lo contiene
      const reportData = Array.isArray(responseData)
        ? responseData
        : responseData?.report || [];

      return {
        report: reportData,
        dateFrom,
        dateTo,
      };
    } catch (error) {
      console.error("Error fetching contacts report:", error);
      return {
        report: [],
        dateFrom,
        dateTo,
      };
    }
  },
};
